import React, { useState, Component } from 'react';
import { Edit2, Flame, TrendingUp, Award, Calendar, Shield, Activity, FileText, MessageSquare, Wand2, Copy, CheckCircle2 } from 'lucide-react';
import { WebsiteHeader } from '../components/Dashboard/WebsiteHeader';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useScans } from '../hooks/useScans';
import { useMessageRewriter } from '../hooks/useMessageRewriter';
export function ProfilePage() {
  const {
    user,
    loading: authLoading
  } = useAuth();
  const {
    showToast
  } = useToast();
  const {
    scans,
    loading
  } = useScans(user?.id);
  const {
    history: rewrites,
    loadingHistory: loadingRewrites
  } = useMessageRewriter();
  const [activeTab, setActiveTab] = useState<'activity' | 'scans' | 'rewrites'>('activity');
  const [copiedRewriteId, setCopiedRewriteId] = useState<string | null>(null);
  const handleCopyRewrite = async (text: string, id?: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedRewriteId(id || null);
      showToast('Rewrite copied to clipboard!', 'success');
      setTimeout(() => setCopiedRewriteId(null), 2000);
    } catch {
      showToast('Failed to copy text', 'error');
    }
  };
  if (authLoading) {
    return <div className="flex flex-col w-full min-h-screen bg-[#050505] relative overflow-hidden" data-id="element-9572" data-mp="31hkuoto6riz5">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(20,20,20,0.5)_1px,transparent_1px),linear-gradient(90deg,rgba(20,20,20,0.5)_1px,transparent_1px)] bg-[size:40px_40px] opacity-50 pointer-events-none" data-id="element-9573" data-mp="3p75h5mjjsyd6" />
        <WebsiteHeader data-id="element-9574" data-mp="3fepyso4ca7rr" />
        <div className="flex items-center justify-center min-h-[60vh]" data-id="element-9575" data-mp="3kfx5ejuqx0wu">
          <div className="text-center" data-id="element-9576" data-mp="3kdu6197pdvfg">
            <div className="w-12 h-12 border-4 border-gray-800 border-t-[#FF0000] rounded-full animate-spin mx-auto mb-4" data-id="element-9577" data-mp="bzllmofc4ayd"></div>
            <p className="text-gray-500 font-mono text-xs uppercase tracking-widest" data-id="element-9578" data-mp="grlrsym0003e">
              Loading profile data...
            </p>
          </div>
        </div>
      </div>;
  }
  if (!user) {
    return <div className="flex flex-col w-full min-h-screen bg-[#050505]" data-id="element-9579" data-mp="loir9qpdsrxx">
        <WebsiteHeader data-id="element-9580" data-mp="gnlb1jnxm7z2" />
        <div className="flex items-center justify-center min-h-[60vh]" data-id="element-9581" data-mp="gn1ua6a0ppmm">
          <div className="text-center" data-id="element-9582" data-mp="clzr5eczctfd">
            <p className="text-gray-500 mb-4 font-mono" data-id="element-9583" data-mp="cp40sr97shdf">
              Unable to load profile
            </p>
            <Link to="/dashboard" className="text-[#FF0000] font-bold hover:underline uppercase tracking-wide text-sm" data-id="element-9584" data-mp="7primjtafyh6">
              Return to Dashboard
            </Link>
          </div>
        </div>
      </div>;
  }
  const firstName = user?.user_metadata?.firstName || 'User';
  const lastName = user?.user_metadata?.lastName || '';
  const fullName = user?.user_metadata?.fullName || `${firstName} ${lastName}`.trim();
  const userEmail = user?.email || '';
  const userInitial = firstName.charAt(0).toUpperCase();
  const joinDate = user?.created_at ? new Date(user.created_at).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric'
  }) : 'Recently';
  const completedScans = scans.filter(s => s.status === 'completed');
  const totalScans = completedScans.length;
  const avgScore = totalScans > 0 ? Math.round(completedScans.reduce((sum, s) => sum + (s.score || 0), 0) / totalScans) : null;
  const bestScore = totalScans > 0 ? Math.min(...completedScans.map(s => s.score || 100)) : null;
  const daysSinceLastScan = scans.length > 0 ? Math.floor((new Date().getTime() - new Date(scans[0].created_at).getTime()) / (1000 * 60 * 60 * 24)) : 0;
  const streak = daysSinceLastScan === 0 && scans.length > 0 ? 1 : 0;
  return <div className="flex flex-col w-full min-h-screen bg-[#050505] relative overflow-hidden" data-id="element-9585" data-mp="y61xs036a1b8">
      {/* Grid Background */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(20,20,20,0.5)_1px,transparent_1px),linear-gradient(90deg,rgba(20,20,20,0.5)_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,#000_70%,transparent_100%)] pointer-events-none fixed" data-id="element-9586" data-mp="30u1wk5on8cnh" />

      <WebsiteHeader data-id="element-9587" data-mp="34uc3plo6993x" />

      <div className="max-w-4xl mx-auto w-full relative z-10" data-id="element-9588" data-mp="34t0ydhf2vtap">
        <main className="px-6 pt-12 pb-24" data-id="element-9589" data-mp="3jiynq0oqup1h">
          {/* Page Header */}
          <div className="text-center mb-10" data-id="element-9590" data-mp="3ekdnw2dem7dk">
            <div className="inline-flex items-center gap-2 mb-4 bg-[#FF0000]/10 border border-[#FF0000]/30 px-3 py-1" data-id="element-9591" data-mp="3ejlrwqfwa7mr">
              <Shield size={14} className="text-[#FF0000]" data-id="element-9592" data-mp="3sjddpdcnksbb" />
              <span className="text-[#FF0000] font-mono text-xs tracking-widest uppercase font-bold" data-id="element-9593" data-mp="3oibdouawwc8a">
                Agent Profile
              </span>
            </div>
            <h1 className="text-3xl font-black text-white mb-2 uppercase tracking-tight" data-id="element-9594" data-mp="1u4jw98mcco2a">
              User Dossier
            </h1>
            <p className="text-gray-400 font-mono text-sm" data-id="element-9595" data-mp="1k1oak3r9knvt">
              Activity logs and performance metrics
            </p>
          </div>

          {/* Profile Card */}
          <div className="bg-[#0a0a0a] p-8 mb-8 border border-gray-800 relative group" data-id="element-9596" data-mp="1ab1y2ieugdhc">
            {/* Corner Brackets */}
            <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-9597" data-mp="1f8v5tfvspfjq" />
            <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-9598" data-mp="10ez07or0z107" />
            <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-9599" data-mp="rcpncs8aavjn" />
            <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-9600" data-mp="wd54zvzlmp7f" />

            <div className="flex items-start justify-between mb-6" data-id="element-9601" data-mp="2qo0m5mounclt">
              <div className="flex items-center gap-6" data-id="element-9602" data-mp="2oa5719g1f3qw">
                <div className="w-20 h-20 bg-[#050505] border border-gray-800 flex items-center justify-center text-white text-3xl font-black shadow-[0_0_20px_rgba(255,0,0,0.1)] relative" data-id="element-9603" data-mp="2obycilewzhxb">
                  {userInitial}
                  <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-[#FF0000] border border-black" data-id="element-9604" data-mp="2fbjsrqhlmavx"></div>
                </div>
                <div data-id="element-9605" data-mp="37yw3n3eecu9e">
                  <h2 className="font-bold text-2xl text-white uppercase tracking-wide mb-1" data-id="element-9606" data-mp="37xcmlakx2f6k">
                    {fullName}
                  </h2>
                  <p className="text-gray-500 text-sm font-mono mb-2" data-id="element-9607" data-mp="331nlh2t90q85">
                    {userEmail}
                  </p>
                  <div className="flex items-center gap-2" data-id="element-9608" data-mp="2t9zx5arznpaw">
                    <Calendar size={12} className="text-gray-600" data-id="element-9609" data-mp="2t7dplb53l9so" />
                    <p className="text-gray-600 text-xs font-mono uppercase" data-id="element-9610" data-mp="3qpnlyugd3uzx">
                      Active Since: {joinDate}
                    </p>
                  </div>
                </div>
              </div>
              <Link to="/settings" data-id="element-9611" data-mp="3tav0l5i2y5xo">
                <button className="p-2 hover:bg-gray-900 border border-transparent hover:border-gray-800 transition-all group/edit" data-id="element-9612" data-mp="230pw1lx5sdj">
                  <Edit2 size={18} className="text-gray-600 group-hover/edit:text-white" data-id="element-9613" data-mp="71tzvk1zz18y" />
                </button>
              </Link>
            </div>

            {streak > 0 && <div className="bg-[#FFD700]/5 border border-[#FFD700]/20 p-3 mb-4 flex items-center gap-3" data-id="element-9614" data-mp="g0pad8c2qxi2">
                <Flame size={16} className="text-[#FFD700]" data-id="element-9615" data-mp="kzick4lwv22x" />
                <span className="font-bold text-[#FFD700] text-sm uppercase tracking-wide" data-id="element-9616" data-mp="ptwaado1plar">
                  {streak} day streak active
                </span>
              </div>}

            <div className="h-px bg-gray-900 my-4" data-id="element-9617" data-mp="zrso5d8dauck" />

            <p className="text-gray-500 text-xs font-mono uppercase tracking-widest" data-id="element-9618" data-mp="13schs6tqffty">
              Status: <span className="text-green-500" data-id="element-9619" data-mp="13q05oaannzot">Operational</span>
            </p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 gap-4 mb-8" data-id="element-9620" data-mp="1l291dm6e7u7p">
            <StatCard icon={<TrendingUp size={16} className="text-blue-500" data-id="element-9622" data-mp="1z18p8rm3052t" />} value={totalScans.toString()} label="Total Scans" color="blue" data-id="element-9621" data-mp="1l3shncs1u59h" />
            <StatCard icon={<Activity size={16} className="text-purple-500" data-id="element-9624" data-mp="16ca4rnuhkquc" />} value={avgScore !== null ? avgScore.toString() : '—'} label="Avg. Toxicity" color="purple" data-id="element-9623" data-mp="1u0l0ydp8m2ca" />
            <StatCard icon={<Award size={16} className="text-green-500" data-id="element-9626" data-mp="1g47jv5om82yc" />} value={bestScore !== null ? bestScore.toString() : '—'} label="Best Score" color="green" data-id="element-9625" data-mp="1g2wh996wp18t" />
            <StatCard icon={<Flame size={16} className="text-orange-500" data-id="element-9628" data-mp="in8pphr5ehg3" />} value={streak.toString()} label="Day Streak" color="orange" data-id="element-9627" data-mp="nkhcxwr5lbo7" />
          </div>

          {/* Tabs */}
          <div className="bg-[#0a0a0a] p-1 mb-8 border border-gray-800 flex gap-1" data-id="element-9629" data-mp="2z1ubno93vow1">
            <button onClick={() => setActiveTab('activity')} className={`flex-1 py-3 px-4 text-xs font-bold uppercase tracking-wider transition-all ${activeTab === 'activity' ? 'bg-[#FF0000] text-white' : 'text-gray-500 hover:text-white hover:bg-gray-900'}`} data-id="element-9630" data-mp="2l9w59l6s473k">
              Recent Activity
            </button>
            <button onClick={() => setActiveTab('scans')} className={`flex-1 py-3 px-4 text-xs font-bold uppercase tracking-wider transition-all ${activeTab === 'scans' ? 'bg-[#FF0000] text-white' : 'text-gray-500 hover:text-white hover:bg-gray-900'}`} data-id="element-9631" data-mp="3hzn1ppyms4c0">
              All Scans ({totalScans})
            </button>
            <button onClick={() => setActiveTab('rewrites')} className={`flex-1 py-3 px-4 text-xs font-bold uppercase tracking-wider transition-all ${activeTab === 'rewrites' ? 'bg-[#FF0000] text-white' : 'text-gray-500 hover:text-white hover:bg-gray-900'}`} data-id="element-9632" data-mp="1xk1zfnkvnist">
              Rewrites
            </button>
          </div>

          {/* Content */}
          {activeTab === 'rewrites' ? loadingRewrites ? <div className="bg-[#0a0a0a] p-12 border border-gray-800 text-center relative" data-id="element-9633" data-mp="399ojrrnir3gs">
                <div className="absolute top-0 left-0 w-2 h-2 border-t border-l border-gray-600" data-id="element-9634" data-mp="2ud79z4uz9wwc" />
                <div className="absolute top-0 right-0 w-2 h-2 border-t border-r border-gray-600" data-id="element-9635" data-mp="2zewo4tdhyqon" />
                <div className="absolute bottom-0 left-0 w-2 h-2 border-b border-l border-gray-600" data-id="element-9636" data-mp="2lfonrx5llciv" />
                <div className="absolute bottom-0 right-0 w-2 h-2 border-b border-r border-gray-600" data-id="element-9637" data-mp="2qf15y4lixvf4" />
                <div className="inline-block w-8 h-8 border-2 border-gray-800 border-t-[#FF0000] rounded-full animate-spin mb-4" data-id="element-9638" data-mp="jke5z47nb9bd"></div>
                <p className="text-gray-500 text-xs font-mono uppercase" data-id="element-9639" data-mp="yoz3vs1olt7i">
                  Retrieving rewrites...
                </p>
              </div> : rewrites.length === 0 ? <div className="bg-[#0a0a0a] p-12 border border-gray-800 text-center relative" data-id="element-9640" data-mp="17hkbqd3ryfhk">
                <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-gray-600" data-id="element-9641" data-mp="12rc7oawvbh1n" />
                <div className="absolute top-0 right-0 w-3 h-3 border-t border-r border-gray-600" data-id="element-9642" data-mp="1hfqawmy757iq" />
                <div className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-gray-600" data-id="element-9643" data-mp="1ch5ih9p6rs4u" />
                <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-gray-600" data-id="element-9644" data-mp="1qger9ob2bjys" />
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-900 mb-6 border border-gray-800" data-id="element-9645" data-mp="1mejp6hlb52jy">
                  <Wand2 className="w-8 h-8 text-gray-600" data-id="element-9646" data-mp="3s92y07mx0pjb" />
                </div>
                <h3 className="font-bold text-white mb-2 uppercase tracking-wide" data-id="element-9647" data-mp="3o77uqxlfc22w">
                  No Rewrites Yet
                </h3>
                <p className="text-gray-500 text-sm mb-8 font-mono max-w-xs mx-auto" data-id="element-9648" data-mp="1r7musbpy9oof">
                  Use the Message Rewriter to transform your communication
                  style.
                </p>
                <Link to="/tools/rewriter" className="inline-block bg-[#FF0000] text-white px-6 py-3 font-bold hover:bg-red-600 transition-all shadow-[0_0_15px_rgba(255,0,0,0.3)] uppercase tracking-wider text-sm" data-id="element-9649" data-mp="212ojx7k86qr0">
                  Try Rewriter
                </Link>
              </div> : <div className="space-y-3" data-id="element-9650" data-mp="2ju7m5c6fe0rz">
                {rewrites.map((rewrite, idx) => {
            const isCopied = copiedRewriteId === (rewrite.id || String(idx));
            return <div key={rewrite.id || idx} className="bg-[#0a0a0a] p-4 border border-gray-800 hover:border-gray-600 transition-all group relative" data-id="element-9651" data-mp="2tu8a5bf8p52i" data-mpi={idx}>
                      <div className="absolute left-0 top-0 bottom-0 w-1 bg-gray-800 group-hover:bg-[#FF0000] transition-colors" data-id="element-9652" data-mp="m4sp5afuuh6a" />
                      <div className="pl-3" data-id="element-9653" data-mp="d5ytpne9fwub">
                        <div className="flex items-center justify-between mb-2" data-id="element-9654" data-mp="d6qqv2nia2mp">
                          <div className="flex items-center gap-2" data-id="element-9655" data-mp="885qn3897iy9">
                            <span className="text-[10px] px-2 py-0.5 bg-[#FF0000]/10 text-[#FF0000] border border-[#FF0000]/30 font-bold uppercase tracking-wider" data-id="element-9656" data-mp="15umm02xffmhd">
                              {rewrite.tone}
                            </span>
                            {rewrite.toxicity_score !== undefined && <span className="text-[10px] text-emerald-500 font-mono" data-id="element-9657" data-mp="r6rqyfvy5614">
                                +{rewrite.toxicity_score}% healthier
                              </span>}
                          </div>
                          <div className="flex items-center gap-3" data-id="element-9658" data-mp="1jsue9xjipzm7">
                            <button onClick={e => {
                      e.stopPropagation();
                      handleCopyRewrite(rewrite.rewritten_text, rewrite.id || String(idx));
                    }} className={`flex items-center gap-1.5 px-2.5 py-1 rounded text-[10px] font-bold uppercase tracking-wider transition-all ${isCopied ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-gray-800/50 text-gray-400 hover:text-white hover:bg-gray-800 border border-gray-700'}`} data-id="element-9659" data-mp="14b2tnr2euaw0">
                              {isCopied ? <>
                                  <CheckCircle2 size={12} data-id="element-9660" data-mp="260oqcpzxga26" />
                                  Copied
                                </> : <>
                                  <Copy size={12} data-id="element-9661" data-mp="3vh0rf0743tdr" />
                                  Copy
                                </>}
                            </button>
                            <span className="text-[10px] text-gray-600 font-mono uppercase" data-id="element-9662" data-mp="859ine5z9xpr">
                              {rewrite.created_at ? getTimeAgo(rewrite.created_at) : ''}
                            </span>
                          </div>
                        </div>
                        <p className="text-gray-500 text-xs font-mono mb-1 line-clamp-1" data-id="element-9663" data-mp="3lkpf4a7iv3jb">
                          Original: {rewrite.original_text}
                        </p>
                        <p className="text-white text-sm font-mono line-clamp-2" data-id="element-9664" data-mp="3go7adac6z56z">
                          {rewrite.rewritten_text}
                        </p>
                      </div>
                    </div>;
          })}
              </div> : loading ? <div className="bg-[#0a0a0a] p-12 border border-gray-800 text-center relative" data-id="element-9665" data-mp="3umoi2ivts11o">
              <div className="absolute top-0 left-0 w-2 h-2 border-t border-l border-gray-600" data-id="element-9666" data-mp="3uoj6w8a7wdb1" />
              <div className="absolute top-0 right-0 w-2 h-2 border-t border-r border-gray-600" data-id="element-9667" data-mp="2h2tw5m4ccke" />
              <div className="absolute bottom-0 left-0 w-2 h-2 border-b border-l border-gray-600" data-id="element-9668" data-mp="7fmhsbk9vyfe" />
              <div className="absolute bottom-0 right-0 w-2 h-2 border-b border-r border-gray-600" data-id="element-9669" data-mp="36vaok9lxtzf4" />
              <div className="inline-block w-8 h-8 border-2 border-gray-800 border-t-[#FF0000] rounded-full animate-spin mb-4" data-id="element-9670" data-mp="3awcq4x3bunk9"></div>
              <p className="text-gray-500 text-xs font-mono uppercase" data-id="element-9671" data-mp="3kp1xbfruhf9s">
                Retrieving data...
              </p>
            </div> : activeTab === 'activity' ? scans.length === 0 ? <EmptyState data-id="element-9672" data-mp="2s6wou7jtir2s" /> : <div className="space-y-3" data-id="element-9673" data-mp="2s5v4tdktqu8x">
                {scans.slice(0, 5).map(scan => <ActivityItem key={scan.id} scan={scan} data-id="element-9674" data-mp="2kmcboehv9kmm" />)}
              </div> : scans.length === 0 ? <EmptyState data-id="element-9675" data-mp="2aphfi292fwbi" /> : <div className="space-y-3" data-id="element-9676" data-mp="2aslqbih1dqb5">
              {scans.map(scan => <ActivityItem key={scan.id} scan={scan} showScore data-id="element-9677" data-mp="26odxpa7gjq7n" />)}
            </div>}
        </main>
      </div>
    </div>;
}
// Helper Components
const StatCard = ({
  icon,
  value,
  label,
  color
}: {
  icon: React.ReactNode;
  value: string;
  label: string;
  color: string;
}) => <div className="bg-[#0a0a0a] p-5 border border-gray-800 relative group hover:border-gray-700 transition-colors" data-id="element-9678" data-mp="1s0aw0d0g70on">
    {/* Corner Brackets */}
    <div className="absolute top-0 left-0 w-2 h-2 border-t border-l border-gray-600 opacity-50 group-hover:opacity-100 transition-opacity" data-id="element-9679" data-mp="1izupsvsblfg3" />
    <div className="absolute top-0 right-0 w-2 h-2 border-t border-r border-gray-600 opacity-50 group-hover:opacity-100 transition-opacity" data-id="element-9680" data-mp="1e0rwg8lpcxny" />
    <div className="absolute bottom-0 left-0 w-2 h-2 border-b border-l border-gray-600 opacity-50 group-hover:opacity-100 transition-opacity" data-id="element-9681" data-mp="36w11o1b4qmx6" />
    <div className="absolute bottom-0 right-0 w-2 h-2 border-b border-r border-gray-600 opacity-50 group-hover:opacity-100 transition-opacity" data-id="element-9682" data-mp="2svhusxiehmwl" />

    <div className="flex items-center gap-2 mb-3" data-id="element-9683" data-mp="2j6gf78frp7j5">
      <div className={`w-8 h-8 bg-gray-900 border border-gray-800 flex items-center justify-center`} data-id="element-9684" data-mp="2j5ojlxm679sv">
        {icon}
      </div>
    </div>
    <div className="text-2xl font-black text-white mb-1 font-mono" data-id="element-9685" data-mp="298aayk6hnpb4">{value}</div>
    <div className="text-[10px] text-gray-500 uppercase tracking-widest font-mono" data-id="element-9686" data-mp="2967hfq64ne1n">
      {label}
    </div>
  </div>;
const ActivityItem = ({
  scan,
  showScore = false
}: {
  scan: any;
  showScore?: boolean;
}) => <div className="bg-[#0a0a0a] p-4 border border-gray-800 hover:border-gray-600 transition-all group relative" data-id="element-9687" data-mp="256p35d8ta17s">
    <div className="absolute left-0 top-0 bottom-0 w-1 bg-gray-800 group-hover:bg-[#FF0000] transition-colors" data-id="element-9688" data-mp="1lkdi8eokv1hi" />

    <div className="flex items-center justify-between pl-3" data-id="element-9689" data-mp="1gmlu7vr1zzil">
      <div className="flex-1 min-w-0" data-id="element-9690" data-mp="1go55f7xurkdi">
        <p className="font-bold text-white text-sm truncate uppercase tracking-wide mb-1" data-id="element-9691" data-mp="1ummsd3fxosud">
          {scan.title}
        </p>
        <p className="text-xs text-gray-500 font-mono" data-id="element-9692" data-mp="1qif3n69h7cw5">
          {showScore ? `Score: ${scan.score || 'Processing'} • ` : ''}
          {getTimeAgo(scan.created_at)}
        </p>
      </div>
      <Link to={`/analysis?id=${scan.id}`} className="ml-3 bg-gray-900 text-gray-300 px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#FF0000] hover:text-white transition-all border border-gray-800 hover:border-[#FF0000]" data-id="element-9693" data-mp="1zjv9d0pciqu1">
        View
      </Link>
    </div>
  </div>;
const EmptyState = () => <div className="bg-[#0a0a0a] p-12 border border-gray-800 text-center relative" data-id="element-9694" data-mp="2n7wfk2z1xv6a">
    {/* Corner Brackets */}
    <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-gray-600" data-id="element-9695" data-mp="2utw35un7so7p" />
    <div className="absolute top-0 right-0 w-3 h-3 border-t border-r border-gray-600" data-id="element-9696" data-mp="2uvqovb6b8kcu" />
    <div className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-gray-600" data-id="element-9697" data-mp="2qs0z4idwfwq8" />
    <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-gray-600" data-id="element-9698" data-mp="34raits1bd8yy" />

    <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-900 mb-6 border border-gray-800" data-id="element-9699" data-mp="2ztigs56yg6h1">
      <FileText className="w-8 h-8 text-gray-600" data-id="element-9700" data-mp="2c3o3jmiq4fw9" />
    </div>
    <h3 className="font-bold text-white mb-2 uppercase tracking-wide" data-id="element-9701" data-mp="275359sn186ag">
      No Data Found
    </h3>
    <p className="text-gray-500 text-sm mb-8 font-mono max-w-xs mx-auto" data-id="element-9702" data-mp="2lu9bw1o78ysd">
      Initiate your first scan to generate forensic data.
    </p>
    <Link to="/upload" className="inline-block bg-[#FF0000] text-white px-6 py-3 font-bold hover:bg-red-600 transition-all shadow-[0_0_15px_rgba(255,0,0,0.3)] uppercase tracking-wider text-sm" data-id="element-9703" data-mp="2h0ej88qm99h5">
      Initiate Scan
    </Link>
  </div>;
function getTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 60) return `${diffMins} MIN AGO`;
  if (diffHours < 24) return `${diffHours} HR AGO`;
  if (diffDays < 7) return `${diffDays} DAY${diffDays !== 1 ? 'S' : ''} AGO`;
  return `${Math.floor(diffDays / 7)} WK AGO`;
}